/**
 * Analytics Utility
 *
 * A flexible analytics utility that provides a clean abstraction layer for tracking events.
 * Currently implemented with Google Analytics (gtag), but designed to be easily replaceable
 * with other analytics providers (Mixpanel, Plausible, PostHog, etc.).
 *
 * Usage:
 * - Call initializeAnalytics() once in main.tsx
 * - Use trackEvent() to track custom events
 * - Use trackPageView() to track page views
 * - Use setUsername() and clearUsername() for user identification
 */

import type {
  BaseEventParams,
  EventCategoryType,
  EventActionType,
  PageViewParams,
  UserProperties,
} from '@/types/analytics';

/**
 * Analytics configuration
 */
interface AnalyticsConfig {
  measurementId: string;
  debug?: boolean;
  enabled?: boolean;
}

/**
 * Global gtag function type definition
 */
declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
  }
}

let isInitialized = false;
let config: AnalyticsConfig | null = null;

/**
 * Initialize analytics with Google Analytics
 *
 * @param measurementId - Google Analytics Measurement ID (GA4)
 * @param options - Optional configuration
 */
export function initializeAnalytics(
  measurementId?: string,
  options: { debug?: boolean; enabled?: boolean } = {}
): void {
  // SSR safety check
  if (typeof window === 'undefined') {
    console.warn('[Analytics] Skipping initialization: not in browser environment');
    return;
  }

  // Get measurement ID from parameter or environment variable
  const gaId = measurementId || import.meta.env.VITE_GOOGLE_ANALYTICS_ID;
  
  if (!gaId) {
    console.warn(
      '[Analytics] No measurement ID provided. Set VITE_GOOGLE_ANALYTICS_ID in your .env file'
    );
    return;
  }

  // Check if already initialized
  if (isInitialized) {
    console.warn('[Analytics] Already initialized');
    return;
  }

  try {
    config = {
      measurementId: gaId,
      debug: options.debug ?? import.meta.env.DEV,
      enabled: options.enabled ?? true,
    };

    // Initialize dataLayer
    window.dataLayer = window.dataLayer || [];

    // Define gtag function
    window.gtag = function gtag() {
      window.dataLayer?.push(arguments);
    };

    // Set timestamp
    window.gtag('js', new Date());

    // Configure Google Analytics
    window.gtag('config', gaId, {
      send_page_view: false, // We'll manually track page views for better control
      debug_mode: config.debug,
    });

    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    document.head.appendChild(script);

    isInitialized = true;

    if (config.debug) {
      console.log('[Analytics] Initialized with measurement ID:', gaId);
    }
  } catch (error) {
    console.error('[Analytics] Failed to initialize:', error);
  }
}

/**
 * Check if analytics is initialized and enabled
 */
function isAnalyticsReady(): boolean {
  if (typeof window === 'undefined') return false;
  if (!isInitialized || !config?.enabled) return false;
  if (!window.gtag) {
    console.warn('[Analytics] gtag not loaded yet');
    return false;
  }
  return true;
}

/**
 * Track a custom event
 *
 * @param category - Event category (e.g., 'wallet', 'transaction')
 * @param action - Event action (e.g., 'connect', 'submit')
 * @param params - Additional event parameters
 *
 * @example
 * trackEvent('wallet', 'wallet_connect', {
 *   wallet_provider: 'phantom',
 *   connection_method: 'desktop'
 * });
 */
export function trackEvent(
  category: EventCategoryType | string,
  action: EventActionType | string,
  params?: BaseEventParams
): void {
  if (!isAnalyticsReady()) return;

  try {
    const eventParams = {
      event_category: category,
      ...params,
    };

    window.gtag!('event', action, eventParams);

    if (config?.debug) {
      console.log('[Analytics] Event tracked:', action, eventParams);
    }
  } catch (error) {
    console.error('[Analytics] Failed to track event:', error);
  }
}

/**
 * Track a page view
 *
 * @param path - Page path (e.g., '/dashboard')
 * @param params - Additional page view parameters
 *
 * @example
 * trackPageView('/dashboard', { page_title: 'Dashboard' });
 */
export function trackPageView(path: string, params?: PageViewParams): void {
  if (!isAnalyticsReady()) return;

  try {
    const pageViewParams = {
      page_path: path,
      page_location: window.location.href,
      page_title: params?.page_title || document.title,
      ...params,
    };

    window.gtag!('event', 'page_view', pageViewParams);

    if (config?.debug) {
      console.log('[Analytics] Page view tracked:', pageViewParams);
    }
  } catch (error) {
    console.error('[Analytics] Failed to track page view:', error);
  }
}

/**
 * Set username for tracking authenticated users
 *
 * @param username - Twitter/X username of the authenticated user
 * @param properties - Additional user properties
 *
 * @example
 * setUsername('johndoe', { user_name: 'John Doe' });
 */
export function setUsername(username: string, properties?: UserProperties): void {
  if (!isAnalyticsReady()) return;

  try {
    window.gtag!('config', config!.measurementId, {
      user_id: username,
      ...properties,
    });

    // Also set as user property
    window.gtag!('set', 'user_properties', {
      username: username,
      ...properties,
    });

    if (config?.debug) {
      console.log('[Analytics] Username set:', username, properties);
    }
  } catch (error) {
    console.error('[Analytics] Failed to set username:', error);
  }
}

/**
 * Clear username (e.g., on logout)
 */
export function clearUsername(): void {
  if (!isAnalyticsReady()) return;

  try {
    window.gtag!('config', config!.measurementId, {
      user_id: null,
    });

    if (config?.debug) {
      console.log('[Analytics] Username cleared');
    }
  } catch (error) {
    console.error('[Analytics] Failed to clear username:', error);
  }
}

/**
 * Enable or disable analytics tracking
 *
 * @param enabled - Whether to enable analytics
 */
export function setAnalyticsEnabled(enabled: boolean): void {
  if (config) {
    config.enabled = enabled;
    if (config.debug) {
      console.log('[Analytics] Tracking', enabled ? 'enabled' : 'disabled');
    }
  }
}

/**
 * Get current analytics configuration (for debugging)
 */
export function getAnalyticsConfig(): Readonly<AnalyticsConfig> | null {
  return config ? { ...config } : null;
}

/**
 * Check if analytics is initialized
 */
export function isAnalyticsInitialized(): boolean {
  return isInitialized;
}
